import * as GsmCharsetUtils from '@trt2/gsm-charset-utils';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import SendScheduleConfirmModel from '../../../components/Admin/SendScheduleConfirmModel';
import SMSCounter from '../../../components/Campaign/SMSCounter/SMSCounter';
import CustomAlert from '../../../components/CustomAlert';
import config from '../../../config/Constant';
import { getRequest, postRequest } from '../../../helpers/apiHandler';
import { campaign_calculator } from '../../../helpers/helperFunction';
import { refreshTemplates } from '../../../store/actions/reduxAction';
import { EditIcon, SaveIcon } from '../../SVG/CampaignLogo';
import ScheduleModal from './ScheduleModal';
import TemplateModal from './TemplateModal';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const CreateCampaignForm = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { access_token, user } = useSelector((state) => state.auth);
  const { maxMessageCharacterLength, refreshTemplate } = useSelector((state) => state.campaign);
  const { campaign_form_fields, setCampaignFormFields } = props;
  const { price_per_sms, country, currency } = user;
  const [disabled, setDisabled] = useState(false);
  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [total_sms_price, setTotalSmsPrice] = useState(0);

  const {
    message_content,
    campaign_name,
    campaign_id,
    selected_custom_field,
    selected_contact_list,
    contact_options,
    brand_name,
    template_options,
    selected_template,
    message_count,
    quick_sms_numbers,
    contact_type,
    template_name,
    sms_count,
    schedule_time,
    campaign_price,
    user_rates,
    sms_price_list,
    custom_field_options,
  } = campaign_form_fields;
  const handleCampaignFormFieldsChange = (field, value) => {
    setCampaignFormFields((prevFields) => ({
      ...prevFields,
      [field]: value,
    }));
  };

  const showConfirm = () => {
    closeModal();
    setShowConfirmModel(true);
  };

  const newmessage_content =
    country === 'MY' ? `RM0 ${brand_name}: ${message_content}` : message_content;

  const get_user_rates_by_country = (contact_countries_iso, user_rate_by_country) => {
    return contact_countries_iso
      .map((iso) => {
        const rate = user_rate_by_country.find((r) => r.country === iso);
        return rate
          ? {
              country: rate.country,
              user_rate: rate[currency.iso3.toLowerCase()],
            }
          : null;
      })
      .filter(Boolean);
  };

  const get_contacts_via_id = (lead_list_id) => {
    const data = {
      headers: { Authorization: access_token.token },
      params: { lead_list_id: lead_list_id },
    };
    getRequest('/v2/contacts-overview', data, (res) => {
      if (res.success) {
        const { data } = res;
        var countries = data.data.map((item) => item.country);

        const result = get_user_rates_by_country(countries, sms_price_list, currency.iso3);

        handleCampaignFormFieldsChange('user_rates', result);
        const total_sms_price = result.reduce((sum, item) => sum + item.user_rate, 0);

        handleCampaignFormFieldsChange(
          'campaign_price',
          campaign_calculator({ sms_count: data.data.length, message_count, total_sms_price }).total
        );
      }
    });
  };

  useEffect(() => {
    get_contacts_via_id(selected_contact_list.id);
  }, [selected_contact_list.id]);

  const handleContactSelection = (value) => {
    handleCampaignFormFieldsChange('selected_contact_list', value);
    handleCampaignFormFieldsChange('sms_count', value.count);
    if (value && value.fields) {
      handleCampaignFormFieldsChange(
        'custom_field_options',
        value.fields.map((field) => ({ value: field.value, label: field.label }))
      );
    }
  };

  const textareaRef = useRef(null);
  const [show, setShow] = useState(false);
  const [showScheduleModel, setShowScheduleModel] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const closeModal = () => {
    setShow(false);
    setShowScheduleModel(false);
  };

  useEffect(() => {
    const calculateAndUpdatePrice = () => {
      if (contact_type === 'quick_sms') {
        handleCampaignFormFieldsChange(
          'campaign_price',
          campaign_calculator({
            sms_count: sms_count,
            message_count,
            total_sms_price,
          }).total
        );
      } else {
        handleCampaignFormFieldsChange(
          'campaign_price',
          campaign_calculator({
            sms_count: selected_contact_list.count,
            message_count,
            total_sms_price,
          }).total
        );
      }
    };

    calculateAndUpdatePrice();
  }, [message_count, total_sms_price, sms_count, contact_type, selected_contact_list]);

  useEffect(() => {
    // Get base rate per SMS (not multiplied)
    const base_rate = user_rates.reduce((sum, item) => sum + item.user_rate, 0) / sms_count;
    setTotalSmsPrice(base_rate);
  }, [user_rates, sms_count]);

  const onChangeTextMessage = (message) => {
    message = message.replace(/[\u00A0\u200B-\u200D\u202F\u205F\u3000]/g, '');
    const brand_length = country === 'MY' ? `RM0 ${brand_name}:`.length : 0;
    const fullMessage = country === 'MY' ? `RM0 ${brand_name}: ${message}` : message;
    const smsCountObj = GsmCharsetUtils.getCharCount(fullMessage);

    if (white_label_perkeso && smsCountObj.charCount + brand_length >= 160) return;

    handleCampaignFormFieldsChange('message_content', message);
    handleCampaignFormFieldsChange('message_count', smsCountObj.msgCount);

    if (message.length > maxMessageCharacterLength) {
      setAlertState({
        alertShow: true,
        alertVariant: 'danger',
        alertMessage: `Max message length is ${maxMessageCharacterLength}`,
      });
    }
  };

  const onChangeBrand = (brand_name) => {
    handleCampaignFormFieldsChange('brand_name', brand_name);
  };

  const saveTemplate = (alertmessage) => {
    const data = {
      template_id: selected_template?.id || null,
      message_content: message_content,
      name: template_name,
    };

    postRequest('/v2/template-save', data, access_token.token, (res) => {
      if (res.success) {
        if (!refreshTemplate) {
          dispatch(refreshTemplates());
        }
        setAlertState({
          message: alertmessage,
          show: true,
          variant: 'success',
        });
        const selected_template = {};
        selected_template.label = template_name;
        handleCampaignFormFieldsChange('selected_template', selected_template);
      } else {
        setAlertState({
          message: 'There was an error saving new message template.',
          show: true,
          variant: 'danger',
        });
      }
      setShow(false);
    });
  };

  const deleteTemplate = () => {
    const data = {
      template_id: selected_template?.id || null,
    };

    postRequest('/v2/template-delete', data, access_token.token, (res) => {
      if (res.success) {
        if (!refreshTemplate) {
          dispatch(refreshTemplates());
        }
        setAlertState({
          message: `The ${template_name} template has been successfully deleted`,
          show: true,
          variant: 'success',
        });
        handleCampaignFormFieldsChange('selected_template', null);
        handleCampaignFormFieldsChange('message_content', message_content);
      } else {
        setAlertState({
          message: 'There was an error deleting message template.',
          show: true,
          variant: 'danger',
        });
      }
      setShow(false);
    });
  };

  const handleSelectField = (field) => {
    let message = message_content;
    if (field === null) {
      const fieldPlaceholder = `{${selected_custom_field?.value}}`;
      message = message.replace(fieldPlaceholder, '');
      onChangeTextMessage(message);
      handleCampaignFormFieldsChange('selected_custom_field', null);
    } else {
      const field_key = `{${field.value}}`;
      const { selectionStart, selectionEnd } = textareaRef.current;
      message = message.substr(0, selectionStart) + field_key + message.substr(selectionStart);
      onChangeTextMessage(message);
      handleCampaignFormFieldsChange('selected_custom_field', field);
    }
  };
  const onChangeCampaignName = (evt) => {
    const { value } = evt.target;
    handleCampaignFormFieldsChange('campaign_name', value);
  };

  const handleRadioChange = (evt) => {
    const { value } = evt.target;

    handleCampaignFormFieldsChange('contact_type', value);
  };

  const handleQuickSMS = (evt) => {
    var phone_numbers = evt.target.value.replace(/[^0-9 :,]/g, '');
    const comma_count = (phone_numbers.match(/,/g) || []).length;
    const sms_count = comma_count + 1;

    handleCampaignFormFieldsChange('quick_sms_numbers', phone_numbers);
    handleCampaignFormFieldsChange('sms_count', sms_count);

    phone_numbers = phone_numbers.split(',');
    var countries = [];

    // Collect valid countries
    for (let i = 0; i < phone_numbers.length; i++) {
      const phone = phone_numbers[i];
      const phone_info = parsePhoneNumberFromString(`+${phone}`, { extract: true });
      if (phone_info && isValidPhoneNumber(phone_info.number)) {
        countries.push(phone_info.country);
      }
    }

    // Update rates once after collecting all countries
    const result = get_user_rates_by_country(countries, sms_price_list, currency.iso3);
    handleCampaignFormFieldsChange('user_rates', result);
    // Price calculation will be handled by useEffect
  };

  const onChangeTemplate = (value) => {
    handleCampaignFormFieldsChange('selected_template', value || null);
    let newMessageContent = value?.message_content || '';
    handleCampaignFormFieldsChange('message_content', newMessageContent);
    handleCampaignFormFieldsChange('template_name', value?.name || '');
  };

  const campaign_statuses = {
    draft: 1,
    in_progress: 2,
    scheduled: 4,
  };

  const handleSaveDraft = () => {
    handleCreateCampaign(campaign_statuses.draft, 'SMS Campaign successfully saved as a draft');
  };

  const handleScheduleLater = () => {
    handleCreateCampaign(
      campaign_statuses.scheduled,
      'SMS Campaign successfully scheduled for later'
    );
  };

  const handleSendNow = () => {
    handleCreateCampaign(campaign_statuses.in_progress, 'SMS Campaign successfully sent');
  };

  const handleScheduleModal = () => {
    if (new Date(campaign_form_fields.schedule_time) < new Date()) {
      const t = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      handleCampaignFormFieldsChange('schedule_time', t);
    }
    setShowScheduleModel(true);
  };

  const handleCreateCampaign = (campaign_status, successMessage = '') => {
    if (!campaign_name) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Please add a Campaign Name.',
      });
      return;
    }

    if (campaign_name && campaign_name.length <= 2) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Campaign name is required and should be more than 2 characters.',
      });
      return;
    }
    if (
      contact_type === 'contacts' &&
      !selected_contact_list.id &&
      campaign_status === campaign_statuses.draft
    ) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Select contacts or a number to save a draft.',
      });
      return;
    }
    if (contact_type === 'contacts' && !selected_contact_list.id) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Select contacts or a number to start your campaign.',
      });
      return;
    }
    if (contact_type === 'quick_sms' && quick_sms_numbers.trim() === '') {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Select contacts or a number to start your campaign.',
      });
      return;
    }
    if (!white_label_perkeso && country === 'MY' && !brand_name && brand_name === '') {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'A brand name is required for your campaign.',
      });
      return;
    }
    if (!message_content && message_content === '') {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Please add message to launch the campaign.',
      });
      return;
    }
    if (campaign_status === campaign_statuses.scheduled && !schedule_time && schedule_time === '') {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'Select a date and time to schedule your campaign.',
      });
      return;
    }

    if (contact_type === 'quick_sms') {
      const numbers = quick_sms_numbers.split(',');
      const phone_numbers = [];

      for (const phone of numbers) {
        const phone_info = parsePhoneNumberFromString(`+${phone}`, { extract: true });
        if (phone_info && isValidPhoneNumber(phone_info.number)) {
          phone_numbers.push(phone_info.number);
        } else {
          setAlertState({
            show: true,
            variant: 'danger',
            message: 'One of your mobile numbers was not valid',
          });
          return;
        }
      }
    }

    const data = {
      campaign_name: campaign_name,
      contact_list_id: selected_contact_list?.id,
      quick_sms_numbers: quick_sms_numbers,
      message_content: message_content,
      campaign_id: campaign_id,
      campaign_status: campaign_status,
      brand_name: brand_name,
      contact_type: contact_type,
      schedule_time: schedule_time,
    };

    setDisabled(true);
    postRequest('/v2/campaign-create', data, access_token.token, (res) => {
      if (res.success) {
        setAlertState({
          show: true,
          variant: 'success',
          message: successMessage,
        });
        setTimeout(() => {
          navigate('/dashboard/campaign/overview');
        }, 1000);
      } else {
        setAlertState({
          show: true,
          variant: 'danger',
          message: res.message || 'Failed to create campaign. Please try again.',
        });
      }
      setDisabled(false);
    });
  };

  useEffect(() => {
    if (selected_contact_list != '') {
      handleContactSelection(selected_contact_list);
    }

    if (alertState.show) {
      setTimeout(() => {
        setAlertState({
          message: '',
          show: false,
          variant: '',
        });
      }, config.notify_time);
    }

    if (selected_template?.label !== '' && !selected_template?.id) {
      // Find from template_options;
      const newSelected = template_options.find((opt) => opt.label === selected_template?.label);
      if (newSelected !== undefined) {
        handleCampaignFormFieldsChange('selected_template', newSelected);
      }
    }
  }, []);

  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      color: '#BDBDBD',
    }),
    clearIndicator: (provided, state) => ({
      ...provided,
      display: state.selectProps.value ? 'flex' : 'none',
    }),
  };

  useEffect(() => {
    if (parseFloat(campaign_price) > parseFloat(user.available_balance)) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: 'You have insufficient credits',
      });
    }
  }, []);

  return (
    <>
      <CustomAlert
        message={alertState.message}
        show={alertState.show}
        variant={alertState.variant}
        closeAlert={() => setAlertState({ show: false, variant: 'danger', message: '' })}
      />
      <div className="CreateCampaign" style={{ position: 'sticky', top: 0, padding: '15px 30px' }}>
        <Form>
          <div className="title-heading">
            <h3>Choose Contact(s)</h3>
          </div>
          <Form.Group className="contact-type-selection">
            <div
              className="radio-group"
              style={{ display: 'flex', alignItems: 'center', height: '43px' }}
            >
              <Form.Check
                custom
                inline
                id="contact-type-3"
                type="radio"
                label={
                  <>
                    Contact Number(s)
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-quick-sms">
                          Send Quick SMS by entering contact number(s) manually.
                        </Tooltip>
                      }
                    >
                      <span className="ml-2">
                        <i className="fas fa-info-circle"></i>
                      </span>
                    </OverlayTrigger>
                  </>
                }
                name="contact_type"
                value="quick_sms"
                checked={contact_type === 'quick_sms'}
                onChange={handleRadioChange}
              />
              <Form.Check
                custom
                inline
                id="contact-type-1"
                type="radio"
                label={
                  <>
                    Contact List
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-contacts">
                          Select one contact list from the dropdown below.
                        </Tooltip>
                      }
                    >
                      <span className="ml-2">
                        <i className="fas fa-info-circle"></i>
                      </span>
                    </OverlayTrigger>
                  </>
                }
                name="contact_type"
                value="contacts"
                checked={contact_type === 'contacts'}
                onChange={handleRadioChange}
              />
              {contact_type === 'contacts' && (
                <Button
                  variant="ghost"
                  href="/dashboard/contact"
                  style={{ minWidth: 160, marginLeft: '10px' }}
                >
                  Create Contact List
                </Button>
              )}
            </div>
            {contact_type === 'contacts' && (
              <Select
                styles={customStyles}
                placeholder={
                  contact_options && contact_options.length > 0
                    ? 'Select Contacts..'
                    : 'No saved contact lists'
                }
                className="custom-form-control"
                options={contact_options}
                value={selected_contact_list}
                onChange={handleContactSelection}
              />
            )}
            {contact_type === 'quick_sms' && (
              <>
                <div style={{ marginBottom: '8px' }}>
                  <span>
                    Separate each number by commas. Use a country code followed by mobile numbers.
                  </span>
                </div>
                <Form.Control
                  placeholder="601121355891, 601121346781"
                  value={quick_sms_numbers}
                  onChange={handleQuickSMS}
                />
              </>
            )}
          </Form.Group>

          <div className="title-heading">
            <h3>SMS Content</h3>
          </div>

          <Row>
            {user.country === 'MY' ? (
              <>
                <Col xs={12} lg={6}>
                  <Form.Group>
                    <Form.Label>
                      Campaign Name
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-campaign-name">
                            Maximum 30 characters allowed.
                          </Tooltip>
                        }
                      >
                        <span className="ml-2">
                          <i className="fas fa-info-circle"></i>
                        </span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      placeholder="Your Campaign Name"
                      value={campaign_name}
                      onChange={onChangeCampaignName}
                      maxLength={30}
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} lg={6}>
                  <Form.Group>
                    <Form.Label>
                      Brand Name
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-campaign-name">
                            The brand name will appear before the SMS content. Maximum 30 characters
                            allowed.
                          </Tooltip>
                        }
                      >
                        <span className="ml-2">
                          <i className="fas fa-info-circle"></i>
                        </span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                      placeholder="Your Brand Name"
                      value={brand_name}
                      onChange={(evt) => onChangeBrand(evt.target.value)}
                      maxLength={30}
                    />
                  </Form.Group>
                </Col>
              </>
            ) : (
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Campaign Name</Form.Label>
                  <Form.Control
                    placeholder="Your Campaign Name"
                    value={campaign_name}
                    onChange={onChangeCampaignName}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <Form.Group>
                <Form.Label>
                  {' '}
                  Message Template
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="tooltip-campaign-name">
                        To Save a New Template, draft your message below and click the 'save' icon
                        at the bottom right of the message box.
                      </Tooltip>
                    }
                  >
                    <span className="ml-2">
                      <i className="fas fa-info-circle"></i>
                    </span>
                  </OverlayTrigger>
                </Form.Label>
                <Select
                  styles={customStyles}
                  className="custom-form-control"
                  options={template_options}
                  value={selected_template}
                  onChange={(value) => onChangeTemplate(value)}
                  placeholder={
                    template_options && template_options.length > 0
                      ? 'Select Template..'
                      : 'No saved templates'
                  }
                  isClearable={!!selected_template}
                />
              </Form.Group>
            </Col>
            <Col xs={12} lg={6}>
              {contact_type !== 'quick_sms' && (
                <Form.Group>
                  <Form.Label>
                    Custom Fields
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="tooltip-custom-fields">
                          <div style={{ textAlign: 'left' }}>
                            How to use custom fields:
                            <ol>
                              <li>Select a contact.</li>
                              <li>Write a message.</li>
                              <li>Click custom fields dropdown.</li>
                              <li>Select any custom fields. E.g. mobile_number.</li>
                            </ol>
                          </div>
                        </Tooltip>
                      }
                    >
                      <span className="ml-2">
                        <i className="fas fa-info-circle"></i>
                      </span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Select
                    styles={customStyles}
                    placeholder={
                      custom_field_options && custom_field_options.length > 0
                        ? 'Select custom fields..'
                        : 'No saved custom fields.'
                    }
                    className="custom-form-control"
                    options={custom_field_options}
                    value={selected_custom_field}
                    onChange={handleSelectField}
                    isClearable={!selected_custom_field}
                  />
                </Form.Group>
              )}
            </Col>
            <Col xs={12}>
              <Form.Group className="form-textarea">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '5px',
                  }}
                >
                  <Form.Label style={{ margin: 0 }}>Message</Form.Label>
                </div>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Type your message here"
                  value={message_content}
                  onChange={(evt) => onChangeTextMessage(evt.target.value)}
                  ref={textareaRef}
                />
                <div className="footer">
                  <SMSCounter message={newmessage_content} />
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {selected_template?.id
                          ? 'Edit Message Template'
                          : 'Save as Message Template'}
                      </Tooltip>
                    }
                  >
                    <span className="ml-2">
                      {selected_template?.id ? (
                        <EditIcon handleModalEdit={() => setShow(true)} />
                      ) : (
                        <SaveIcon handleModalSave={() => setShow(true)} />
                      )}
                    </span>
                  </OverlayTrigger>
                </div>
              </Form.Group>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '-10px',
                  fontSize: '13px',
                  color: '#BDBDBD',
                }}
              >
                <span>
                  {user.country === 'MY' ? (
                    <>
                      Following Malaysia's SMS regulations, <strong>RM0</strong> and your{' '}
                      <strong>brand name </strong>
                      must be added to your message and will be included in the character count.
                    </>
                  ) : (
                    ''
                  )}
                </span>
              </div>
              <div className="d-flex justify-content-left" style={{ marginTop: 24, gap: 16 }}>
                {parseFloat(campaign_price) > parseFloat(user.available_balance) ? (
                  <>
                    {!white_label_perkeso && (
                      <Button variant="primary" href="/dashboard/payment/buy-credits">
                        Top Up Now
                      </Button>
                    )}
                    <CustomAlert
                      message={alertState.message}
                      show={alertState.show}
                      variant={alertState.variant}
                      closeAlert={() =>
                        setAlertState({ show: false, variant: 'danger', message: '' })
                      }
                    />
                  </>
                ) : (
                  <>
                    <Button
                      onClick={white_label_perkeso ? showConfirm : handleSendNow}
                      variant="primary"
                      style={{ fontWeight: 500 }}
                      disabled={disabled}
                    >
                      Send Now
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleScheduleModal()}
                      style={{ fontWeight: 500 }}
                      disabled={disabled}
                    >
                      Schedule Later
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  onClick={handleSaveDraft}
                  style={{ fontWeight: 500 }}
                  disabled={disabled}
                >
                  Save as Draft
                </Button>
                <ScheduleModal
                  show={showScheduleModel}
                  onClose={closeModal}
                  handleScheduleLater={handleScheduleLater}
                  campaign_form_fields={campaign_form_fields}
                  setCampaignFormFields={setCampaignFormFields}
                />
              </div>
            </Col>
          </Row>
        </Form>

        <TemplateModal
          show={show}
          onClose={closeModal}
          saveTemplate={saveTemplate}
          campaign_form_fields={campaign_form_fields}
          setCampaignFormFields={setCampaignFormFields}
          deleteTemplate={deleteTemplate}
        />
        <SendScheduleConfirmModel
          show={showConfirmModel}
          onClose={() => setShowConfirmModel(false)}
          mode="send_now"
          handleSendNow={handleSendNow}
        />
      </div>
    </>
  );
};

export default CreateCampaignForm;
